// Payment & Details Page — Upload slip and astrology info
// Dynamic pricing based on birth time confidence
import { renderHeader, renderBottomNav } from '../components/header.js';
import { getCurrentUser, updateUserCache } from '../auth.js';
import { isFirebaseReady } from '../firebase.js';
import { getBookingDB, updateBookingDB, updateUserProfileDB, getUser, uploadAstrologyPhoto, getAdminSettingsDB } from '../services/db.js';
import { notifyAstrologyDataSubmitted } from '../services/api.js';
import { toastSuccess, toastError, toastInfo } from '../components/toast.js';
import { navigate } from '../router.js';
import { formatThaiDate } from '../components/calendar.js';
import { t } from '../i18n.js';

// Default fallback pricing tiers (loaded from Firestore if available)
const DEFAULT_PRICING_TIERS = [
    { id: 'has_time', label: 'มีเวลาเกิด (เช่น 8.45 น.)', price: 599, color: '#06C755' },
    { id: 'no_time', label: 'ไม่มีเวลาเกิด / ไม่มั่นใจนาที', price: 999, color: '#f2994a' },
    { id: 'twin', label: 'มีฝาแฝด', price: 999, color: '#f2994a', unit: '/คน' },
    { id: 'abroad_has_time', label: 'เกิดต่างประเทศ มีเวลาเกิด', price: 999, color: '#9b59f0' },
    { id: 'abroad_no_time', label: 'เกิดต่างประเทศ ไม่มีเวลาเกิด', price: 1599, color: '#e94d9e' }
];

export async function renderPayment(params) {
    const bookingId = params.id;
    const currentUser = getCurrentUser();
    const app = document.getElementById('app');

    // Helper: Resize Image Client-side to avoid Firebase Storage & stay under Firestore 1MB limits
    function resizeImageToBase64(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    let width = img.width;
                    let height = img.height;
                    const MAX_SIZE = 600; // Resize to max 600px edge
                    if (width > height) {
                        if (width > MAX_SIZE) { height *= MAX_SIZE / width; width = MAX_SIZE; }
                    } else {
                        if (height > MAX_SIZE) { width *= MAX_SIZE / height; height = MAX_SIZE; }
                    }
                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);
                    resolve(canvas.toDataURL('image/jpeg', 0.8));
                };
                img.onerror = () => reject(new Error('ไฟล์ภาพไม่ถูกต้อง'));
                img.src = e.target.result;
            };
            reader.onerror = () => reject(new Error('อ่านไฟล์ล้มเหลว'));
            reader.readAsDataURL(file);
        });
    }

    if (!isFirebaseReady()) {
        toastError('ระบบขัดข้อง กรุณาลองใหม่');
        navigate('/history');
        return;
    }

    app.innerHTML = `
    ${renderHeader()}
    <div class="page px-md">
      <div style="text-align:center; padding: var(--space-xl);">
        <div class="loading-spinner"></div>
      </div>
    </div>
    ${renderBottomNav('history')}
  `;

    try {
        const booking = await getBookingDB(bookingId);
        if (!booking || booking.user_id !== currentUser.id) {
            toastError('ไม่พบข้อมูลการจองนี้');
            navigate('/history');
            return;
        }

        // Fetch fresh user data and pricing tiers in parallel
        const [userDb, adminSettings] = await Promise.all([
            getUser(currentUser.id),
            getAdminSettingsDB()
        ]);
        const hasPhone = userDb?.phone && userDb.phone.trim().length >= 9;

        // Load pricing tiers from Firestore (admin-managed)
        const pricingTiers = (adminSettings?.pricing_tiers && adminSettings.pricing_tiers.length > 0)
            ? adminSettings.pricing_tiers
            : DEFAULT_PRICING_TIERS;

        // Determine initial birth confidence selection
        const savedBC = booking.customer_details?.birth_confidence || (pricingTiers[0]?.id || 'has_time');
        const savedTier = pricingTiers.find(t => t.id === savedBC) || pricingTiers[0];

        app.innerHTML = `
      ${renderHeader()}
      <div class="page px-md pb-xxl">
        <h2 class="page-title mt-md mb-none">✅ ข้อมูลครบถ้วนแล้ว</h2>
        
        <div class="card mb-lg mt-md" style="text-align:center; padding: 2rem;">
           <div style="font-size: 3rem; margin-bottom: 1rem;">🧾</div>
           <h3 style="color: var(--accent-gold); margin-bottom: 0.5rem;">ระบบได้รับข้อมูลดวงชะตาของท่านเรียบร้อยแล้ว</h3>
           <p style="color: var(--text-secondary); font-size: var(--font-sm); line-height: 1.6;">
              ขณะนี้สามารถปิดหน้าต่างนี้และ<br>
              <strong>ส่งสลิปชำระเงินเข้ามาในแชท LINE</strong> ได้เลยค่ะ
           </p>
           
           <div class="mt-lg">
               <button type="button" class="btn btn-outline btn-block mt-sm" onclick="location.hash='#/history'">กลับไปหน้าประวัติ</button>
           </div>
        </div>
      </div>
      ${renderBottomNav('history')}
    `;

    } catch (err) {
        console.error('Payment Page Error:', err);
        toastError('ไม่สามารถโหลดข้อมูลได้');
        navigate('/history');
    }
}
